import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Link,
  Box,
  Tabs,
  Tab,
  useTheme,
  Alert,
  CircularProgress,
  Modal,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { 
  Close as CloseIcon, 
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon 
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const categories = ['All', 'Rune', 'Runita'];

function Gallery() {
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/gallery/images.json');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch images.json: ${response.status}`);
        }
        
        const data = await response.json();
        
        const validImages = data.images
          .filter(img => img.id && img.url && img.category && img.artist)
          .map(img => ({
            ...img,
            url: encodeURI(img.url),
            originalUrl: img.url
          }))
          .sort((a, b) => {
            // Sort by ID in reverse order (newest first)
            // Handle both string and numeric IDs
            const idA = isNaN(a.id) ? a.id : Number(a.id);
            const idB = isNaN(b.id) ? b.id : Number(b.id);
            
            if (typeof idA === 'string' && typeof idB === 'string') {
              return idB.localeCompare(idA);
            } else if (typeof idA === 'number' && typeof idB === 'number') {
              return idB - idA;
            } else {
              // Mixed types, convert to string for comparison
              return String(idB).localeCompare(String(idA));
            }
          });
        
        if (validImages.length !== data.images.length) {
          console.warn(`${data.images.length - validImages.length} images were filtered out due to missing required fields`);
        }
        
        setImages(validImages);
        console.log(`Loaded ${validImages.length} images (last updated: ${data.lastUpdated})`);
        
      } catch (error) {
        console.error('Error loading gallery images:', error);
        setError(error.message);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const filteredImages = images.filter(
    (img) => category === 'All' || img.category === category
  );

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedImage(null), 200); // Delay to allow animation
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && modalOpen) {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modalOpen]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading gallery...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h2"
        component={motion.h2}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        sx={{ mb: 4, color: 'text.primary', textAlign: 'center' }}
      >
        Art Gallery
      </Typography>

      {/* Fanart Rules Accordion */}
      <Box sx={{ mb: 4 }}>
        <Accordion 
          sx={{ 
            backgroundColor: 'background.paper',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="fanart-rules-content"
            id="fanart-rules-header"
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: 'primary.contrastText',
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Fanart Rules
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    Rune Rules
                  </Typography>
                  <img
                    src="/gallery/1rune_rules.png"
                    alt="Rune Fanart Rules"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      boxShadow: theme.shadows[3],
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={() => handleImageClick({
                      id: 'rune-rules',
                      url: '/gallery/1rune_rules.png',
                      category: 'Rules',
                      artist: 'Runerek',
                      artistUrl: null
                    })}
                    onError={(e) => {
                      console.error('Failed to load Rune rules image');
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    Runita Rules
                  </Typography>
                  <img
                    src="/gallery/2runita_rules.png"
                    alt="Runita Fanart Rules"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      boxShadow: theme.shadows[3],
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={() => handleImageClick({
                      id: 'runita-rules',
                      url: '/gallery/2runita_rules.png',
                      category: 'Rules',
                      artist: 'Runerek',
                      artistUrl: null
                    })}
                    onError={(e) => {
                      console.error('Failed to load Runita rules image');
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Error loading gallery: {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={category}
          onChange={(e, newValue) => setCategory(newValue)}
          centered
          textColor="primary"
          indicatorColor="primary"
        >
          {categories.map((cat) => (
            <Tab key={cat} value={cat} label={cat} />
          ))}
        </Tabs>
      </Box>

      {filteredImages.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            {category === 'All' ? 'No images found' : `No ${category} images found`}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {images.length === 0 
              ? 'No images available in the gallery'
              : 'Try selecting a different category'
            }
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {filteredImages.map((image, index) => (
          <Grid item xs={12} sm={6} md={4} key={image.id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.2s ease-in-out',
                    boxShadow: theme.shadows[8],
                  },
                }}
                onClick={() => handleImageClick(image)}
              >
                <CardMedia
                  component="img"
                  height="300"
                  image={image.url}
                  alt={`Art by ${image.artist}`}
                  sx={{ 
                    objectFit: 'cover',
                    backgroundColor: 'grey.200'
                  }}
                  onError={(e) => {
                    console.error(`Failed to load image: ${image.originalUrl || image.url}`);
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.alt = `Image failed to load: ${image.artist}`;
                  }}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    By {image.artist}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {image.category}
                  </Typography>
                  {image.artistUrl && (
                    <Link
                      href={image.artistUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      onClick={(e) => e.stopPropagation()} // Prevent modal from opening
                    >
                      Artist Profile
                    </Link>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {images.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Gallery last updated: May 15, 2023 • {images.length} total images
          </Typography>
        </Box>
      )}

      {/* Image Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box sx={{ outline: 'none', maxWidth: '90vw', maxHeight: '90vh' }}>
          <AnimatePresence>
            {selectedImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    overflow: 'hidden',
                    position: 'relative',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  {/* Close Button */}
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {/* Image */}
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.100',
                      minHeight: { xs: '50vh', md: '60vh' },
                      maxHeight: { xs: '50vh', md: '80vh' },
                    }}
                  >
                    <img
                      src={selectedImage.url}
                      alt={`Art by ${selectedImage.artist}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        e.target.alt = 'Image failed to load';
                      }}
                    />
                  </Box>

                  {/* Image Information */}
                  <Box
                    sx={{
                      width: { xs: '100%', md: '350px' },
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        Artwork Details
                      </Typography>
                      <Divider />
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Artist
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        {selectedImage.artist}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Category
                      </Typography>
                      <Chip 
                        label={selectedImage.category} 
                        color="primary" 
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Box>

                    {selectedImage.artistUrl && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Artist Profile
                        </Typography>
                        <Link
                          href={selectedImage.artistUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Visit Artist Profile
                          <OpenInNewIcon fontSize="small" />
                        </Link>
                      </Box>
                    )}

                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        Click outside or press ESC to close
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Modal>
    </Container>
  );
}

export default Gallery;